/**
 * @fileoverview TemplateNotesFeed — toggleable right-hand sidebar inside
 * the precedent template edit dialog. Lets multiple practitioners post
 * comment-style notes against a single template.
 *
 * Modelled on `ContactNotesFeed` but trimmed: no AI summary, no chat
 * button, just composer + chronological feed.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  NotesEditor,
  type NotesEditorHandle,
} from "@/ui/composed/editor/notes-editor";
import { TemplateNoteRow, type TemplateNote } from "./TemplateNoteRow";

interface TemplateNotesFeedProps {
  templateId: Id<"precedentTemplates">;
  currentUserId?: Id<"users">;
  onClose?: () => void;
}

function isHtmlEmpty(html: string): boolean {
  const stripped = html
    .replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return stripped.length === 0;
}

export function TemplateNotesFeed({
  templateId,
  currentUserId,
  onClose,
}: TemplateNotesFeedProps) {
  const [composerDraft, setComposerDraft] = useState<string>("");
  const composerRef = useRef<NotesEditorHandle | null>(null);

  const { data: notes, isLoading } = useQuery({
    ...convexQuery(api.settings.precedents.notes.listTemplateNotes, {
      templateId,
    }),
  });

  const createNote = useConvexMutation(
    api.settings.precedents.notes.createTemplateNote,
  );

  const { mutateAsync: doCreate, isPending: isPosting } = useMutation({
    mutationFn: (content: string) => createNote({ templateId, content }),
  });

  const handlePost = useCallback(async () => {
    if (isHtmlEmpty(composerDraft)) {
      toast.info("Write something before posting.");
      return;
    }
    try {
      await doCreate(composerDraft);
      setComposerDraft("");
      composerRef.current?.getEditor()?.commands.clearContent();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not post your note";
      toast.error(message);
    }
  }, [composerDraft, doCreate]);

  // Cmd / Ctrl + Enter to post — re-attached when handlePost identity
  // changes so the listener captures the latest draft.
  useEffect(() => {
    const editor = composerRef.current?.getEditor();
    if (!editor) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void handlePost();
      }
    };
    const dom = editor.view.dom;
    dom.addEventListener("keydown", onKeyDown);
    return () => dom.removeEventListener("keydown", onKeyDown);
  }, [handlePost]);

  const composerIsEmpty = isHtmlEmpty(composerDraft);

  const notesList: Array<TemplateNote> = useMemo(
    () =>
      (notes ?? []).map((n) => ({
        _id: n._id,
        content: n.content,
        createdBy: n.createdBy,
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
        authorDisplayName: n.authorDisplayName,
        authorAvatarUrl: n.authorAvatarUrl ?? undefined,
        lastEditedByName: n.lastEditedByName ?? undefined,
      })),
    [notes],
  );

  return (
    <section className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Notes
          </h3>
          <Badge variant="secondary" size="sm">
            {notesList.length}
          </Badge>
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={onClose}
            aria-label="Close notes"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <NotesEditor
          ref={composerRef}
          value={composerDraft}
          onChange={setComposerDraft}
          placeholder="Add a note about this template — what it's for, when to use it, anything worth remembering."
          minHeightClass="min-h-[100px]"
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">
            Cmd/Ctrl + Enter to post
          </span>
          <Button
            size="sm"
            onClick={() => void handlePost()}
            disabled={isPosting || composerIsEmpty}
            className="gap-1.5"
          >
            {isPosting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
            Post note
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {isLoading ? (
          <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-muted/10 py-6 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading notes…
          </div>
        ) : notesList.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/10 p-6 text-center text-sm text-muted-foreground">
            No notes yet. Post the first one above to start the journal.
          </div>
        ) : (
          notesList.map((n) => (
            <TemplateNoteRow
              key={n._id}
              note={n}
              canEdit={!!currentUserId && n.createdBy === currentUserId}
            />
          ))
        )}
      </div>
    </section>
  );
}
